<template>
  <section class="section">
    <div class="container">

      <div class="columns is-multiline is-mobile mb-5">
        <div class="column is-12-mobile">
          <div class="field">
            <div class="control has-icons-left has-icons-right">
              <input 
                class="input is-small" 
                type="search" 
                placeholder="Pesquisar empresas" 
                v-model="localSearch"
                @input="handleSearchInput"
              >
              <span class="icon is-small is-left">
                <i class="fas fa-search fa-xs"></i>
              </span>
              <span class="icon is-small is-right is-clickable" v-if="localSearch" @click="clearSearch">
                <i class="fas fa-times fa-xs"></i>
              </span>
            </div>
          </div>
        </div>
        <div class="column is-6-mobile">
          <div class="select is-small" :class="{'is-loading': isFiltering}">
            <select v-model="state" @change="setFiltering">
              <option value="">Todos os estados</option>
              <option value="active">Ativo</option>
              <option value="near-expire">A expirar (≤30 dias)</option>
              <option value="inactive">Expirado</option>
            </select>
          </div>
        </div>
        <div class="column is-6-mobile">
          <div class="select is-small" :class="{'is-loading': isFiltering}">
            <select v-model="sort" @change="setFiltering">
              <option value="remaining-days-asc">Dias restantes (menor-maior)</option>
              <option value="remaining-days-desc">Dias restantes (maior-menor)</option>
              <option value="name-asc">Nome A-Z</option>
              <option value="name-desc">Nome Z-A</option>
            </select>
          </div>
        </div>
      </div>

      <div class="mb-3" v-if="localSearch || state">
        <p class="has-text-grey is-size-7">
          <span v-if="searchResults.length > 0">
            <span class="has-text-weight-bold">{{ searchResults.length }}</span> empresa{{ searchResults.length !== 1 ? 's' : ''}} encontrada{{ searchResults.length !== 1 ? 's' : ''}}
          </span>
          <span v-else>
            Nenhuma empresa encontrada
          </span>
        </p>
      </div>

      <div v-if="isFiltering" class="has-text-centered py-6">
        <span class="icon is-large">
          <i class="fas fa-spinner fa-spin fa-2x has-text-primary"></i>
        </span>
        <p class="has-text-grey mt-3">A filtrar empresas...</p>
      </div>

      <div v-else-if="searchResults.length === 0 && (localSearch || validity || state)" class="has-text-centered py-6">
        <span class="icon is-large">
          <i class="fas fa-search fa-2x has-text-grey-light"></i>
        </span>
        <p class="has-text-grey mt-3">Nenhuma empresa encontrada com os filtros atuais.</p>
        <button class="button is-small is-link mt-3" @click="clearAllFilters">Limpar filtros</button>
      </div>

      <div v-else-if="companies && companies.length === 0" class="has-text-centered py-6">
        <span class="icon is-large">
          <i class="fas fa-building fa-2x has-text-grey-light"></i>
        </span>
        <p class="has-text-grey mt-3">Nenhuma empresa disponível.</p>
      </div>

      <div v-else>
        <div class="columns header-companies is-hidden-touch">
          <div class="column is-2 has-text-centered">Estado</div>
          <div class="column is-5">Empresa</div>
          <div class="column is-2"></div>
          <div class="column is-1"></div>
          <div class="column is-2"></div>
        </div>
        <div>
          <CompaniesRow :company="company" v-for="company of searchResults" :key="company.id_goldylocks"></CompaniesRow>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import CompaniesRow from "@/components/dashboard/CompaniesRow";

export default {
  name: "CompaniesTable",
  components: {CompaniesRow},
  props: ['companies'],
  data() {
    return {
      localSearch: '',
      isFiltering: false,
      debounceTimer: null,
      state: ''
    }
  },
  computed: {
    search: {
      get() {
        return this.$store.getters.search
      },
      set(val) {
        this.$store.commit('search', val)
      }
    },

    sort: {
      get() {
        return this.$store.getters.sort
      },
      set(val) {
        this.$store.commit('sort', val)
      }
    },
    state: {
      get() {
        return this.$data.state
      },
      set(val) {
        this.$data.state = val
      }
    },
    searchResults() {
      let filteredCompanies = this.companies ? [...this.companies] : [];

      const getRemainingDays = (company) => {
        const days = company && company.modules && company.modules.vendas && company.modules.vendas.remainingDays;
        return parseInt(days) || 0;
      };

      const searchFields = [
        'nome_goldylocks',
        'nif_goldylocks',
        'alias_goldylocks',
        'agente_email',
        'agente_nome',
        'contabilista_email',
        'contabilista_nome'
      ];

      if (this.search && this.search.length > 0) {
        const searchString = this.search.toLowerCase();
        filteredCompanies = filteredCompanies.filter(company =>
          searchFields.some(field => {
            const value = company[field];
            return value && value.toLowerCase().includes(searchString);
          })
        );
      }

      if (this.state) {
        filteredCompanies = filteredCompanies.filter(company => {
          if (this.state === 'active') {
            return company.active === true;
          } else if (this.state === 'inactive') {
            return company.active === false;
          } else if (this.state === 'near-expire') {
            const remainingDays = getRemainingDays(company);
            return remainingDays > 0 && remainingDays <= 30;
          }
          return true;
        });
      }

      if (this.sort === 'remaining-days-asc') {
        filteredCompanies.sort((a, b) => {
          const daysA = getRemainingDays(a);
          const daysB = getRemainingDays(b);
          return daysA - daysB;
        });
      } else if (this.sort === 'remaining-days-desc') {
        filteredCompanies.sort((a, b) => {
          const daysA = getRemainingDays(a);
          const daysB = getRemainingDays(b);
          return daysB - daysA;
        });
      } else if (this.sort === 'name-asc') {
        filteredCompanies.sort((a, b) => {
          const nameA = (a.nome_goldylocks || '').toString().toLowerCase();
          const nameB = (b.nome_goldylocks || '').toString().toLowerCase();
          return nameA.localeCompare(nameB);
        });
      } else if (this.sort === 'name-desc') {
        filteredCompanies.sort((a, b) => {
          const nameA = (a.nome_goldylocks || '').toString().toLowerCase();
          const nameB = (b.nome_goldylocks || '').toString().toLowerCase();
          return nameB.localeCompare(nameA);
        });
      }

      return filteredCompanies;
    }
  },
  methods: {
    handleSearchInput() {
      this.isFiltering = true;

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(() => {
        this.search = this.localSearch;
        this.isFiltering = false;
      }, 300);
    },
    setFiltering() {
      this.isFiltering = true;
      setTimeout(() => {
        this.isFiltering = false;
      }, 150);
    },
    clearSearch() {
      this.localSearch = '';
      this.search = '';
    },
    clearAllFilters() {
      this.localSearch = '';
      this.search = '';
      this.state = '';
      this.sort = 'remaining-days-asc';
    }
  },
  mounted() {
    this.localSearch = this.search;
    this.$store.commit('context', 'usercompanies');
    this.$store.dispatch('fetchProfile');
  }
}
</script>

<style scoped>
.header-companies {
  background-color: #F5F6FA;
  color: #8D90A1;
  font-weight: bold;
  border-radius: 10px;
}

.header-companies > .columns > .column {
  /*padding-left: 50px;*/
}

.is-clickable {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.is-clickable:hover {
  opacity: 0.7;
}

.py-6 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.py-6 .icon {
  transition: transform 0.3s ease;
}

.py-6:hover .icon {
  transform: scale(1.1);
}
</style>
